import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Navbar from "../components/ui/Navbar";
import Footer from "../components/ui/Footer";

const validationSchema = Yup.object({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  message: Yup.string().required("Message is required"),
  phone: Yup.string()
    .matches(/^\d{10,15}$/, "Phone number must be 10-15 digits")
    .nullable(),
  companyName: Yup.string().max(100, "Company name too long").nullable(),
});

const ContactPage = () => {
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      companyName: "",
      message: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await fetch("https://queryminds.com/contact", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(values),
        });

        const data = await response.json();
        if (response.ok) {
          alert("Message sent successfully!");
          formik.resetForm();
        } else {
          alert(data.error || "Failed to send message.");
        }
      } catch (error) {
        alert("An error occurred. Please try again later.");
      }
    },
  });

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Navbar */}
      <Navbar />

      {/* Main Content */}
      <div className="flex-grow container mx-auto py-20 px-4">
        <h1 className="text-4xl font-bold mb-6 text-center">Get in Touch</h1>
        <p className="text-lg mb-4 text-gray-700 text-center">
          We’re here to help. Reach out to us for any inquiries or to discuss how Query Minds can support your business.
        </p>

        {/* Contact Form */}
        <form
          className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-lg"
          onSubmit={formik.handleSubmit}
        >
          {/* First Name */}
          <div className="mb-4">
            <label htmlFor="firstName" className="block text-gray-700 font-medium mb-2">
              First Name *
            </label>
            <input
              id="firstName"
              type="text"
              placeholder="First Name"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              {...formik.getFieldProps("firstName")}
            />
            {formik.touched.firstName && formik.errors.firstName && (
              <p className="text-red-500 text-sm">{formik.errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div className="mb-4">
            <label htmlFor="lastName" className="block text-gray-700 font-medium mb-2">
              Last Name *
            </label>
            <input
              id="lastName"
              type="text"
              placeholder="Last Name"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              {...formik.getFieldProps("lastName")}
            />
            {formik.touched.lastName && formik.errors.lastName && (
              <p className="text-red-500 text-sm">{formik.errors.lastName}</p>
            )}
          </div>

          {/* Email */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
              Email *
            </label>
            <input
              id="email"
              type="email"
              placeholder="Email"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              {...formik.getFieldProps("email")}
            />
            {formik.touched.email && formik.errors.email && (
              <p className="text-red-500 text-sm">{formik.errors.email}</p>
            )}
          </div>

          {/* Phone (Optional) */}
          <div className="mb-4">
            <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
              Phone (Optional)
            </label>
            <input
              id="phone"
              type="text"
              placeholder="Phone Number"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              {...formik.getFieldProps("phone")}
            />
            {formik.touched.phone && formik.errors.phone && (
              <p className="text-red-500 text-sm">{formik.errors.phone}</p>
            )}
          </div>

          {/* Company Name (Optional) */}
          <div className="mb-4">
            <label htmlFor="companyName" className="block text-gray-700 font-medium mb-2">
              Company Name (Optional)
            </label>
            <input
              id="companyName"
              type="text"
              placeholder="Company Name"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              {...formik.getFieldProps("companyName")}
            />
            {formik.touched.companyName && formik.errors.companyName && (
              <p className="text-red-500 text-sm">{formik.errors.companyName}</p>
            )}
          </div>

          {/* Message */}
          <div className="mb-4">
            <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
              Message *
            </label>
            <textarea
              id="message"
              placeholder="Your Message"
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="5"
              {...formik.getFieldProps("message")}
            ></textarea>
            {formik.touched.message && formik.errors.message && (
              <p className="text-red-500 text-sm">{formik.errors.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-blue-600 text-white p-2 rounded hover:bg-blue-700 transition"
            aria-label="Submit Contact Form"
          >
            Send Message
          </button>
        </form>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ContactPage;
